import React from 'react';
import type { RechargePlan } from './rechargePlans';

export interface PlanCardProps {
  plan: RechargePlan;
  language?: 'en' | 'zh';
  isDarkMode?: boolean;
  onPrimaryAction: (plan: RechargePlan) => void;
  loadingAmount?: number | null;
  disabled?: boolean;
  actionLabelOverride?: { zh: string; en: string };
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  language = 'zh',
  isDarkMode = true,
  onPrimaryAction,
  loadingAmount = null,
  disabled = false,
  actionLabelOverride,
}) => {
  const isEn = language === 'en';
  const label = (zh: string, en: string) => (isEn ? en : zh);

  const cardBg = isDarkMode ? '#1a1a1a' : '#ffffff';
  const cardBorderNormal = isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const cardBorderHighlight = isDarkMode
    ? '1px solid rgba(215, 205, 161, 0.65)'
    : '1px solid rgba(215, 205, 161, 0.8)';
  const highlightShadow = isDarkMode
    ? '0 0 24px rgba(215, 205, 161, 0.12)'
    : '0 0 24px rgba(215, 205, 161, 0.18)';

  const creditsBg = isDarkMode ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.04)';
  const creditsBorder = isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)';
  const creditsTitleColor = isDarkMode ? '#8e8e93' : '#6b7280';
  const creditsAmountColor = isDarkMode ? '#D7CDA1' : '#7a7558';
  const titlesColor = isDarkMode ? '#a1a1a6' : '#6b7280';
  const amountColor = isDarkMode ? '#ffffff' : '#0f172a';
  const featureTextColor = isDarkMode ? '#b0b0b5' : '#6b7280';
  const iconTextColor = isDarkMode ? 'text-white' : 'text-black/60';

  const loading = loadingAmount === plan.amount;
  const highlight = plan.cardHighlight;
  const btnZh = actionLabelOverride?.zh ?? plan.primaryCta.zh;
  const btnEn = actionLabelOverride?.en ?? plan.primaryCta.en;

  return (
    <div
      className="relative flex flex-col rounded-xl p-5 sm:p-6 min-h-[420px]"
      style={{
        background: cardBg,
        border: highlight ? cardBorderHighlight : cardBorderNormal,
        boxShadow: highlight ? highlightShadow : undefined,
      }}
    >
      {plan.badge && (
        <div
          className="absolute -top-px right-4 rounded-b-md px-2.5 py-1 text-[11px] font-semibold tracking-wide"
          style={{
            background: 'linear-gradient(135deg, #D7CDA1, #a39d7a)',
            color: '#111',
          }}
        >
          {label(plan.badge.zh, plan.badge.en)}
        </div>
      )}

      <p className="text-[13px] mb-1 pr-8" style={{ color: titlesColor }}>
        {label(plan.titles.zh, plan.titles.en)}
      </p>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4">
        <div className="text-3xl sm:text-4xl font-bold" style={{ color: amountColor }}>
          ¥{plan.amount}
        </div>
        {plan.listPrice != null && (
          <span
            className="text-lg sm:text-xl font-medium line-through"
            style={{ color: isDarkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)' }}
          >
            ¥{plan.listPrice}
          </span>
        )}
      </div>

      <div className="rounded-lg px-3 py-3 mb-5" style={{ background: creditsBg, border: creditsBorder }}>
        <div className="text-[11px] mb-1" style={{ color: creditsTitleColor }}>
          {label('总计获得积分', 'Total points')}
        </div>
        <div className="text-lg font-semibold" style={{ color: creditsAmountColor }}>
          <span className="text-2xl font-bold">{plan.credits}</span> {label('积分', 'pts')}
        </div>
        {plan.bonusHint && (
          <p className="mt-2 text-xs leading-snug" style={{ color: isDarkMode ? '#D7CDA1' : '#6b6648' }}>
            {label(plan.bonusHint.zh, plan.bonusHint.en)}
          </p>
        )}
      </div>

      <ul className="space-y-2.5 mb-10 shrink-0">
        {plan.featureRows.map((row, idx) => {
          const main = isEn ? row.main.en : row.main.zh;
          const unit = row.unit ? (isEn ? row.unit.en : row.unit.zh) : null;
          const icon = row.leadingIcon ?? 'check';
          return (
            <li
              key={`${plan.id}-${idx}`}
              className="flex gap-2 text-[13px] leading-snug"
              style={{ color: featureTextColor }}
            >
              <span className={`mt-0.5 shrink-0 ${iconTextColor}`}>
                {icon === 'clock' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                )}
              </span>
              <div className="flex min-w-0 flex-1 justify-between gap-3 items-baseline">
                <span className="min-w-0">{main}</span>
                {unit != null && unit !== '' && (
                  <span className="shrink-0 text-[12px] tabular-nums opacity-95">{unit}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        disabled={loading || disabled}
        onClick={() => onPrimaryAction(plan)}
        className="mt-auto w-full shrink-0 py-3.5 rounded-lg font-bold text-[15px] transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        style={
          plan.ctaStyle === 'gold'
            ? {
                background: 'linear-gradient(135deg, #D7CDA1, #b8b28a)',
                color: '#111',
                boxShadow: '0 4px 20px rgba(215, 205, 161, 0.35)',
              }
            : {
                background: '#ffffff',
                color: '#111',
                border: isDarkMode ? undefined : '1px solid rgba(0,0,0,0.08)',
              }
        }
      >
        {loading ? (
          <span className="inline-flex items-center justify-center gap-2">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            {label('跳转中…', 'Redirecting…')}
          </span>
        ) : (
          label(btnZh, btnEn)
        )}
      </button>
    </div>
  );
};

export default PlanCard;
